import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Ensures doctor name has "Dr." prefix without duplication
 * @param {string} name - Doctor name (may or may not have "Dr." prefix)
 * @returns {string} - Name with single "Dr." prefix
 */
export function ensureDrPrefix(name) {
  if (!name) return '';
  
  // Remove any existing "Dr." or "Dr " prefix (case insensitive)
  const cleanName = name.replace(/^dr\.?\s*/i, '').trim();
  
  // Add "Dr." prefix
  return cleanName ? `Dr. ${cleanName}` : '';
}

/**
 * Convert date from YYYY-MM-DD format to DD/MM/YYYY format
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @returns {string} - Date in DD/MM/YYYY format
 */
export function formatDateToDDMMYYYY(dateStr) {
  if (!dateStr) return '';
  
  // If already in DD/MM/YYYY format, return as is
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
  
  // Handle YYYY-MM-DD format directly (avoid Date object timezone issues)
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    const [datePart] = dateStr.split('T'); // Handle ISO datetime by taking date part
    const [year, month, day] = datePart.split('-');
    return `${day}/${month}/${year}`;
  }
  
  // Fallback: Handle other formats using Date object
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr; // Return original if invalid
  
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  
  return `${day}/${month}/${year}`;
}

/**
 * Convert date from DD/MM/YYYY format to YYYY-MM-DD format
 * @param {string} dateStr - Date in DD/MM/YYYY format
 * @returns {string} - Date in YYYY-MM-DD format
 */
export function formatDateToYYYYMMDD(dateStr) {
  if (!dateStr) return '';
  
  // Check if already in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  
  // Parse DD/MM/YYYY format
  const parts = dateStr.split('/');
  if (parts.length !== 3) return dateStr; // Return original if invalid format
  
  const [day, month, year] = parts;
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
}

/**
 * Get today's date in DD/MM/YYYY format
 * @returns {string} - Today's date in DD/MM/YYYY format
 */
export function getTodayDDMMYYYY() {
  const today = new Date();
  const day = String(today.getDate()).padStart(2, '0');
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const year = today.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * Get today's date in YYYY-MM-DD format
 * @returns {string} - Today's date in YYYY-MM-DD format
 */
export function getTodayYYYYMMDD() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format leave dates as readable ranges
 * @param {string[]} leaveDates - Array of dates in YYYY-MM-DD format
 * @returns {string} - Formatted leave dates (e.g., "Jan 15, Jan 20 - Jan 22")
 */
export function formatLeaveDates(leaveDates) {
  if (!leaveDates || leaveDates.length === 0) return '';
  
  // Sort dates
  const sorted = [...leaveDates].sort();
  
  // If only 1-2 dates, show them directly
  if (sorted.length <= 2) {
    return sorted.map(date => {
      const d = new Date(date);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }).join(', ');
  }
  
  // For 3+ dates, show as range or list
  const ranges = [];
  let rangeStart = sorted[0];
  let rangeEnd = sorted[0];
  
  for (let i = 1; i < sorted.length; i++) {
    const current = new Date(sorted[i]);
    const previous = new Date(sorted[i - 1]);
    const diffDays = (current - previous) / (1000 * 60 * 60 * 24);
    
    if (diffDays === 1) {
      // Consecutive date, extend range
      rangeEnd = sorted[i];
    } else {
      // Gap found, save current range and start new one
      if (rangeStart === rangeEnd) {
        ranges.push(new Date(rangeStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
      } else {
        const start = new Date(rangeStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const end = new Date(rangeEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        ranges.push(`${start} - ${end}`);
      }
      rangeStart = sorted[i];
      rangeEnd = sorted[i];
    }
  }
  
  // Add final range
  if (rangeStart === rangeEnd) {
    ranges.push(new Date(rangeStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
  } else {
    const start = new Date(rangeStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const end = new Date(rangeEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    ranges.push(`${start} - ${end}`);
  }
  
  return ranges.join(', ');
}
